import { authFetch } from 'src/auth/utils';
import { envVariables } from 'src/common/utils/envVariables';
import { TariffSummaryListDTO } from './tariffSummaryDTOModels';
import qs from 'qs';
import { parseISO } from 'date-fns';
import {
  SortingParam,
  parseSortingParamsToString,
} from 'src/common/components/Filters/constants/sorting';

import { TARIFF_SUMMARY_COLUMN_NAMES_TO_FILTER_MAP } from '../constants/tariffSummaryConstant';
import { TariffSumaryApiSorting, TariffSummaryColumnName } from '../models/tariffSummaryModels';
import { RESPONSE_CODE } from 'src/common/responseCodes';
import { AlertStatusValue } from 'src/atoms/filters/tariff-summary';
import { ConfigureTariffSummaryAlert } from '../hooks/useTariffSummaryAlertConfiguration';

type FetchTafiffSummaryProps = {
  accountRef?: string;
  page: number;
  pageSize?: number;
  sorting: SortingParam<TariffSummaryColumnName>;
  alertStatus?: AlertStatusValue;
};

export const fetchTafiffSummary = async (
  { accountRef, page, pageSize, sorting, alertStatus }: FetchTafiffSummaryProps,
  signal?: AbortSignal,
) => {
  const searchParams: {
    PageNumber: number;
    PageSize: number;
    Order?: string;
    AlertStatus?: string;
  } = {
    PageNumber: page + 1,
    PageSize: pageSize ?? 25,
    AlertStatus: alertStatus ?? 'alertAny',
  };

  if (sorting) {
    const parsedSortings: TariffSumaryApiSorting = {
      sort: sorting.sort,
      columnName: TARIFF_SUMMARY_COLUMN_NAMES_TO_FILTER_MAP[sorting.columnName],
    };
    searchParams['Order'] = parseSortingParamsToString(parsedSortings);
  }

  const tariffSummaryResponse = await authFetch(
    `${envVariables.VITE_USAGE_TRACKING_MODULE_URL}/${accountRef}/aggregated?${qs.stringify(
      searchParams,
    )}`,
    {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const tariffSummaryDTO: TariffSummaryListDTO = await tariffSummaryResponse.json();

  return {
    totalNumberOfItems: tariffSummaryDTO.totalProducts,
    items: tariffSummaryDTO.products.map((row) => ({
      bundleProductCode: row.bundleProductCode,
      tariffDescription: row.tariffDescription,
      alertConfiguration: {
        dataThreshold: row.alertConfiguration?.dataThreshold ?? null,
        smsThreshold: row.alertConfiguration?.smsThreshold ?? null,
        voiceThreshold: row.alertConfiguration?.voiceThreshold ?? null,
      },
      activeCount: row.activeCount,
      usedData: row.data.used,
      forecastData: row.data.forecast,
      allowanceData: row.data.allowance,
      consumptionLevelUsedUsed: row.data.consumptionLevelUsed,
      consumptionLevelForecastUsed: row.data.consumptionLevelForecast,
      sent: row.sms.sent,
      forecastSMS: row.sms.forecast,
      allowanceSMS: row.sms.allowance,
      consumptionLevelSent: row.sms.consumptionLevelSent,
      consumptionLevelForecastSMS: row.sms.consumptionLevelForecast,
      usedSeconds: row.voice.usedSeconds,
      forecastSeconds: row.voice.forecastSeconds,
      allowanceSeconds: row.voice.allowanceSeconds,
      consumptionLevelUsedSeconds: row.voice.consumptionLevelUsed,
      consumptionLevelForecastUsedSeconds: row.voice.consumptionLevelForecast,
      productType: row.productType,
      productCodes: row.productCodes,
    })),
  };
};

export const fetchTariffSummaryLastUpdateTime = async (signal?: AbortSignal) => {
  const tariffSummaryLastUpdateResponse = await authFetch(
    `${envVariables.VITE_USAGE_TRACKING_MODULE_URL}/aggregated/last-update-date`,
    {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (tariffSummaryLastUpdateResponse.status === RESPONSE_CODE.NO_CONTENT) {
    return null;
  }

  const tariffSummaryLastUpdateDTO: string = await tariffSummaryLastUpdateResponse.json();

  return parseISO(tariffSummaryLastUpdateDTO);
};

export type TafiffSummary = Omit<
  Awaited<ReturnType<typeof fetchTafiffSummary>>,
  'lastUpdatedTimestamp'
> & {
  lastUpdatedTimestamp: null | Date;
};

export const configureTariffSummaryAlert = async (
  accountRef: string | null,
  data: ConfigureTariffSummaryAlert,
  signal?: AbortSignal,
) => {
  return Promise.all(
    data.tariffCodes.map(async (tariffCode) => {
      const response = await authFetch(
        `${envVariables.VITE_USAGE_TRACKING_MODULE_URL}/alerts/${accountRef}/${tariffCode}`,
        {
          signal,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data.alertConfiguration),
        },
      );

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(
          `Failed to configure alert for ${tariffCode}: ${response.status} ${errorBody}`,
        );
      }

      return response;
    }),
  );
};
